import { ChevronDownIcon } from '@chakra-ui/icons';
import { Text } from '@chakra-ui/react';
import { useCallback } from 'react';

import { Menu, MenuButton, MenuItem, MenuList } from '@revelio/ui';

import {
  PreferenceScope,
  UserPreferenceKey,
  useUserPreferences,
} from '../../../../../shared/user-preferences';
import { PreferenceBrowserStorage } from '../../../../../shared/user-preferences/utils/preference-session-storage';
import { useTabContentConfig } from '../../../components/PageConfigProvider/PageConfigProvider';
import type { GeographyMode } from '../../hooks/useGeographyMode';
import { useSupportsStateGeography } from '../../hooks/useSupportsStateGeography';
import styles from './GeographyModeSelect.module.css';

const OPTIONS: { value: GeographyMode; label: string }[] = [
  { value: 'msa', label: 'Metro Area' },
  { value: 'administrative', label: 'State' },
];

/**
 * Renders an inline dropdown that replaces the branch label in the 3rd popout
 * panel of the geography tree, allowing users to switch between State and
 * Metro Area granularity directly from the tree.
 */
export const GeographyModeBranchLabel = () => {
  const { revelioPageId } = useTabContentConfig();
  const supportsStateGeography = useSupportsStateGeography(revelioPageId);
  const { preferences } = useUserPreferences(PreferenceScope.GLOBAL);

  const currentMode: GeographyMode =
    preferences[UserPreferenceKey.Geography] === 'administrative'
      ? 'administrative'
      : 'msa';

  const currentLabel =
    OPTIONS.find((o) => o.value === currentMode)?.label ?? 'Metro Area';

  const onSelect = useCallback((mode: GeographyMode) => {
    PreferenceBrowserStorage.update(
      PreferenceScope.GLOBAL,
      UserPreferenceKey.Geography,
      mode
    );
  }, []);

  if (!supportsStateGeography) {
    // Show static "Metro Area" label when state is not supported on this page
    return <Text className={styles.staticLabel}>Metro Area</Text>;
  }

  return (
    <Menu>
      <MenuButton>
        <button type="button" className={styles.trigger}>
          {currentLabel}
          <ChevronDownIcon boxSize={3} />
        </button>
      </MenuButton>
      <MenuList
        side="bottom"
        align="start"
        portal={false}
        className={styles.menu}
      >
        {OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => onSelect(option.value)}
            className={
              option.value === currentMode ? styles.itemSelected : undefined
            }
          >
            {option.label}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
};
